import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Alert, AlertColor, Snackbar } from '@mui/material';

interface SnackbarMessage {
  key: number;
  text: string;
  severity: AlertColor;
}

interface SnackbarContextValue {
  success: (text: string) => void;
  error: (text: string) => void;
  warning: (text: string) => void;
  info: (text: string) => void;
}

const SnackbarContext = createContext<SnackbarContextValue | null>(null);

export function useSnackbar(): SnackbarContextValue {
  const ctx = useContext(SnackbarContext);
  if (!ctx) {
    throw new Error('useSnackbar must be used inside <SnackbarProvider>');
  }
  return ctx;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const SnackbarProvider: React.FC<ProviderProps> = ({ children }) => {
  const [queue, setQueue] = useState<SnackbarMessage[]>([]);
  const [current, setCurrent] = useState<SnackbarMessage | null>(null);

  const push = useCallback((text: string, severity: AlertColor) => {
    setQueue((q) => [...q, { key: Date.now() + Math.random(), text, severity }]);
  }, []);

  // Pull the next message from the queue whenever we go idle.
  useEffect(() => {
    if (current === null && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue((q) => q.slice(1));
    }
  }, [current, queue]);

  const handleClose = useCallback((_e: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setCurrent(null);
  }, []);

  const value = useMemo<SnackbarContextValue>(
    () => ({
      success: (t) => push(t, 'success'),
      error: (t) => push(t, 'error'),
      warning: (t) => push(t, 'warning'),
      info: (t) => push(t, 'info'),
    }),
    [push],
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Snackbar
        key={current?.key}
        open={!!current}
        autoHideDuration={current?.severity === 'error' ? 6000 : 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {current ? (
          <Alert
            severity={current.severity}
            onClose={() => setCurrent(null)}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {current.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
